import { clubhouseViewerIds } from "../../../shared/presentation/clubhouse-feed-order.js";
import { RivalInsightsCarousel } from "../insights/rival-insights-carousel.js";
import { ClubhouseNextAction } from "./clubhouse-next-action.js";
import { rememberRelation } from "../rivalry/comparison-selection.js";
import { rememberInsight } from "../rivalry/insight-navigation-browser.js";
import type { RivalInsight } from "../../../shared/presentation/rival-insight.js";
import { ClubhouseSeasonSummary } from "./clubhouse-season-summary.js";
import { useState } from "react";
import type {
  ProductPageDataMap,
  RoundListItem,
} from "../../../shared/contracts/product-pages.js";
import type { ProductRoute } from "../../app/product-router.js";
import type { RoundWorkspace } from "../../app/use-round-workspace.js";
import { useRoundFollow } from "../../app/use-round-follow.js";
import { roundListItemOf } from "../../lib/round-view.js";
import { isRoundOpen } from "../../../domain/round/round-types.js";
import { Button } from "@/components/ui/button";
import { useClubhouseHistory } from "./clubhouse-comparison-api.js";
import {
  clubhousePeople,
  type ClubhousePerson,
} from "./clubhouse-relations-model.js";
import { ClubhouseRivals } from "./clubhouse-approved-cards.js";
import { ComparisonDialog } from "./clubhouse-comparison.js";
import { RoundFeedCarousel } from "../feed/round-feed-carousel.js";
import "../../styles/clubhouse-approved.css";
import "../../styles/clubhouse-details.css";
import "../../styles/clubhouse-evidence.css";

interface ClubhouseProps {
  data: ProductPageDataMap["clubhouse"];
  workspace: RoundWorkspace;
  route: ProductRoute;
  generatedAt: string;
  onAddRival(): void;
  onOpenRound(round: RoundListItem): void;
  onOpenRoundId(id: string): void;
  reload(): void;
}

/** Approved A3 composition; every record and source comes from the product API. */
export function ClubhousePage(props: ClubhouseProps): React.JSX.Element {
  const history = useClubhouseHistory(props.generatedAt);
  const people = clubhousePeople(history.relations, history.rivals);
  const [selected, setSelected] = useState<ClubhousePerson | null>(null);
  const [selectedInsight, setSelectedInsight] = useState<RivalInsight | null>(
    null,
  );
  const active = props.workspace.snapshot ?? props.data.activeRound;
  const open = active && isRoundOpen(active.state) ? active : null;
  const followed =
    open && open.scorekeeperId !== props.workspace.accountId ? open : null;
  useRoundFollow(followed, props.reload);
  const pick = (person: ClubhousePerson) => {
    if (person.relation) {
      openSharedRecord(
        props,
        person.relation.id,
        () => setSelected(null),
        null,
      );
      return;
    }
    setSelectedInsight(null);
    setSelected(person);
  };
  return (
    <div className="fr-home">
      <header className="fr-home-hero">
        <h1>
          Every rivalry
          <br />
          starts with
          <br />
          <em>one round.</em>
        </h1>
      </header>
      <div className="fr-home-content">
        <ClubhouseNextAction
          accountId={props.workspace.accountId}
          queued={props.workspace.queued}
          rounds={history.rounds}
          onResult={props.onOpenRound}
          round={open}
          followed={Boolean(followed)}
          onOpen={() =>
            followed
              ? props.onOpenRound(roundListItemOf(followed))
              : props.route.navigate(open ? "scorecard" : "tee-off")
          }
        />
        <ClubhouseHistorySections
          props={props}
          history={history}
          people={people}
          onSelect={pick}
          onAll={() => {
            props.route.navigate("rivalry");
            rememberRelation(null);
          }}
        />
      </div>
      <ClubhouseComparison
        selected={selected}
        shell={props}
        finding={selectedInsight}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

function ClubhouseComparison({
  selected,
  shell,
  finding,
  onClose,
}: {
  selected: ClubhousePerson | null;
  shell: ClubhouseProps;
  finding: RivalInsight | null;
  onClose(): void;
}): React.JSX.Element {
  return (
    <ComparisonDialog
      relationId={selected?.relation?.id ?? null}
      person={selected}
      finding={finding}
      onClose={onClose}
      onOpenRecord={(id) => openSharedRecord(shell, id, onClose, finding)}
      onOpenSource={(id) => {
        onClose();
        shell.onOpenRoundId(id);
      }}
      onStart={() => startForPerson(shell, selected, onClose)}
    />
  );
}

function startForPerson(
  props: ClubhouseProps,
  person: ClubhousePerson | null,
  onClose: () => void,
): void {
  const id = person?.rival?.participantId;
  onClose();
  props.route.navigate("tee-off");
  if (id)
    window.history.replaceState(
      null,
      "",
      `/tee-off?vs=${encodeURIComponent(id)}`,
    );
}

function openSharedRecord(
  props: ClubhouseProps,
  id: string,
  onClose: () => void,
  finding: RivalInsight | null,
): void {
  onClose();
  props.route.navigate("rivalry");
  rememberRelation(id);
  if (finding) rememberInsight(finding);
}

function ClubhouseHistorySections({
  props,
  history,
  people,
  onSelect,
  onAll,
}: {
  props: ClubhouseProps;
  history: ReturnType<typeof useClubhouseHistory>;
  people: ClubhousePerson[];
  onSelect(person: ClubhousePerson): void;
  onAll(): void;
}): React.JSX.Element {
  if (history.loading)
    return (
      <p className="fr-home-loading" role="status">
        Loading your clubhouse…
      </p>
    );
  if (history.error)
    return (
      <div className="fr-home-loading" role="alert">
        <p>{history.error}</p>
        <Button variant="outline-gold" onClick={props.reload}>
          Try again
        </Button>
      </div>
    );
  return (
    <>
      <ClubhouseRivals
        people={people}
        onSelect={onSelect}
        onAll={onAll}
        onAdd={props.onAddRival}
        loading={history.relationsLoading}
        error={history.relationsError}
        onRetry={props.reload}
        availableYears={history.availableYears}
        hasSimulator={history.hasSimulator}
      />
      <RoundFeedCarousel
        viewerParticipantIds={clubhouseViewerIds(
          props.workspace.bootstrap?.participants ?? [],
          props.workspace.accountId,
          history.relations,
        )}
        revision={props.generatedAt}
        onOpen={props.onOpenRoundId}
        onAll={() => props.route.navigate("feed")}
      />
      {!history.insightLoading ? (
        <RivalInsightsCarousel
          findings={
            history.findings ?? (history.finding ? [history.finding] : [])
          }
          onOpen={(finding) =>
            openSharedRecord(
              props,
              finding.relationId,
              () => undefined,
              finding,
            )
          }
        />
      ) : null}
      <ClubhouseSeasonSummary
        accountId={props.workspace.accountId}
        revision={props.generatedAt}
        onOpen={() => props.route.navigate("fgl")}
      />
    </>
  );
}
